/**
 * ForgeFloatingButton Component
 *
 * Floating action button (FAB) that triggers FORGE panel
 * Supports keyboard shortcut (Cmd+K / Ctrl+K)
 */

import React, { useState } from 'react';
import { useCmdK } from '../hooks/useKeyboardShortcut';
import { ForgePanel } from './ForgePanel';
import type { ForgeFloatingButtonProps, PageContext } from '../types/forge';
import styles from '../styles/forge.module.css';

export const ForgeFloatingButton: React.FC<ForgeFloatingButtonProps> = ({
  position = 'bottom-right',
  defaultMode = 'quick',
  serviceId,
}) => {
  const [isPanelOpen, setIsPanelOpen] = useState<boolean>(false);
  const [pageContext, setPageContext] = useState<PageContext | null>(null);

  const handleOpen = () => {
    // Capture page context when opening
    const context: PageContext = {
      url: window.location.href,
      title: document.title,
      timestamp: new Date().toISOString(),
      serviceId,
    };
    setPageContext(context);
    setIsPanelOpen(true);
  };

  const handleClose = () => {
    setIsPanelOpen(false);
    setPageContext(null);
  };

  // Register Cmd+K / Ctrl+K keyboard shortcut
  useCmdK(handleOpen);

  return (
    <>
      <button
        onClick={handleOpen}
        className={`${styles.forgeFab} ${styles[`forgeFab--${position}`]}`}
        aria-label="Open FORGE panel"
        title="Open FORGE (Cmd+K / Ctrl+K)"
      >
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className={styles.forgeFabIcon}
        >
          {/* Anvil/Forge icon */}
          <path
            d="M4 18L4 20C4 21.1046 4.89543 22 6 22L18 22C19.1046 22 20 21.1046 20 20L20 18"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          />
          <path
            d="M12 2L12 18"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          />
          <path
            d="M8 6L16 6"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          />
          <path
            d="M6 10L18 10"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          />
          <path
            d="M4 14L20 14"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          />
        </svg>
        <span className={styles.forgeFabLabel}>FORGE</span>
      </button>

      <ForgePanel
        isOpen={isPanelOpen}
        onClose={handleClose}
        defaultMode={defaultMode}
        serviceId={serviceId}
        pageContext={pageContext || undefined}
      />
    </>
  );
};

export default ForgeFloatingButton;
